import { motion } from "framer-motion";
import { Mail, Newspaper, TrendingUp } from "lucide-react";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import Bubbles from "../components/Bubbles";
import JumpingFish from "../components/JumpingFish";
import MediaKitPanel from "../components/MediaKitPanel";
import PressMentionsFeed from "../components/PressMentionsFeed";
import AwardsShowcase from "../components/AwardsShowcase";
import FundingHistorySection from "../components/FundingHistorySection";
import Breadcrumbs from "../components/Breadcrumbs";
import { AWARDS } from "../data/awards";
import { trackEvent } from "../utils/analytics";
import useDocumentTitle from "../hooks/useDocumentTitle";

const TRACTION = [
  { label: "Partner Fishermen", value: "60+" },
  { label: "Coastal Cities", value: "12" },
  { label: "Avg. Delivery Time", value: "18hrs" },
  { label: "Founded", value: "2021" },
];

export default function PressInvestors() {
  useDocumentTitle("Press & Investor Relations");
  return (
    <PageTransition>
      <section className="relative overflow-hidden bg-gradient-to-b from-ocean-950 to-ocean-900 pt-32 pb-24 text-center text-foam-50 sm:pt-40 sm:pb-28">
        <Bubbles count={12} />
        <JumpingFish className="right-[12%] bottom-10 hidden sm:block" size={36} delay={0.5} flip />
        <div className="relative mx-auto max-w-2xl px-6">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs font-semibold uppercase tracking-widest text-teal-300"
          >
            Press &amp; Investor Relations
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-3 font-display text-4xl font-bold sm:text-5xl"
          >
            The ZenFresh Story, for the Record
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-foam-100/70"
          >
            Coverage, company facts, and how to reach us for media or investment enquiries.
          </motion.p>
        </div>
      </section>

      <Breadcrumbs />

      {/* Traction snapshot */}
      <section className="bg-foam-50 py-16 dark:bg-ocean-950">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 px-6 sm:grid-cols-4">
          {TRACTION.map((stat, i) => (
            <ScrollReveal key={stat.label} delay={i * 0.08} className="text-center">
              <p className="font-display text-3xl font-bold text-ocean-900 sm:text-4xl dark:text-foam-50">{stat.value}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-ocean-700/70 sm:text-sm dark:text-foam-100/70">
                {stat.label}
              </p>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Press: media kit + contact */}
      <section className="mx-auto max-w-6xl px-6 py-24 dark:bg-ocean-950">
        <ScrollReveal>
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500 dark:bg-teal-400/20 dark:text-teal-300">
            <Newspaper className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <h2 className="mt-4 font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Press</h2>
          <p className="mt-3 max-w-2xl text-ocean-800/70 dark:text-foam-100/70">
            For interview requests, brand assets, or fact-checking, our media kit and press
            contact are below. We typically respond within two business days.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="mailto:press@zenfresh.com"
              onClick={() => trackEvent("press_contact_click")}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-ocean-900 ring-1 ring-ocean-900/15 transition-colors hover:bg-ocean-900/5 dark:text-foam-50 dark:ring-white/15 dark:hover:bg-white/10"
            >
              <Mail className="h-4 w-4" />
              press@zenfresh.com
            </a>
          </div>
          <div className="mt-6">
            <MediaKitPanel />
          </div>
        </ScrollReveal>
      </section>

      {/* In the News */}
      <section className="bg-foam-100 py-24 dark:bg-ocean-900">
        <div className="mx-auto max-w-4xl px-6">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
              In the News
            </h2>
          </ScrollReveal>
          <div className="mt-10">
            <PressMentionsFeed />
          </div>
        </div>
      </section>

      {/* Awards — hidden entirely while empty */}
      {AWARDS.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-24 dark:bg-ocean-950">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="font-display text-3xl font-bold text-ocean-950 sm:text-4xl dark:text-foam-50">
              Awards &amp; Recognition
            </h2>
          </ScrollReveal>
          <div className="mt-10">
            <AwardsShowcase />
          </div>
        </section>
      )}

      {/* Investor Relations */}
      <section className="bg-foam-100 py-24 dark:bg-ocean-900">
        <div className="mx-auto max-w-4xl px-6">
          <ScrollReveal>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500 dark:bg-teal-400/20 dark:text-teal-300">
              <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <h2 className="mt-4 font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Investor Relations</h2>
            <p className="mt-3 max-w-2xl text-ocean-800/70 dark:text-foam-100/70">
              ZenFresh is building the direct link between coastal fisheries and the table. Below
              is our public growth snapshot; for deck requests or investment enquiries, reach our
              team directly.
            </p>
            <div className="mt-6">
              <a
                href="mailto:investors@zenfresh.com"
                onClick={() => trackEvent("investor_contact_click")}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-ocean-900 ring-1 ring-ocean-900/10 transition-transform hover:scale-105 dark:bg-ocean-950 dark:text-foam-50 dark:ring-white/10"
              >
                <Mail className="h-4 w-4" />
                investors@zenfresh.com
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} className="mt-12">
            <FundingHistorySection />
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}
